import React from 'react';
import type { NextPage } from 'next';
import WildcardPageWrapper from '../components/WildcardPageWrapper';
import type { LanguageLink } from '../context/LanguageLinksContext';
import { getDestinations, getFaqs, getFeedbackStats, getFestivals, getInvisibleDestinations, getJobs, getYears, getWpOptionsPages, getWpPagesByCategory, getYlCabins, getYlDestinations, getYachtListSlim, getDealsEndAt, hydrateWpPageAcf, mergeKittyConfigIntoOptions, getSeoPages, resolveSeoPageByPath, getSeoPageTranslations, getWpPageTranslations, wpPageLanguageRedirect, wpPagePathSegments, englishPathForTranslatedSlug, canonicalSeoPathForSlug, seoPagePathSegments, seoEnglishRedirectFor, getActiveLanguages, WP_CACHE_TAGS, WP_SLIM_PAGE_FIELDS } from '../helpers/api';
import { buildLastMinuteDeals } from '../helpers/deals';
import { buildScheduleWeeks, publicDestinationIds, ScheduleWeek } from '../helpers/schedule';
import { seoBlocksUseLiveWeeks, withResolvedSeoTokens } from '../helpers/seoTokens';
import { attachYachtLayoutPrices } from '../helpers/yachtPrices';
import { buildOngoingDealsFromSlim, DEFAULT_COMPANY_ID } from '../helpers/ongoingDeals';
import { DestinationType, FeedbackStats, TYWDestinationType, TYWOptionPages, WpPageType } from '../globalTypes';
import wp, { sanitizeDestinationResponse } from '../helpers/wp';
import { sanitizeWpResponse } from '../helpers/wp';
import { getArtists } from 'day8-react/build/content/api';
import { FAQ_LAYOUTS, IntercomArticleType } from '../components/flexible-components/components/FAQs';
import { CATEGORY_PRODUCT_2025, CATEGORY_DESTINATION_2025, CATEGORY_TOFU_2025, CATEGORY_MOFU_2025, CATEGORY_TOFU_V1_2025, CATEGORY_ONSHORE_EVENTS, CATEGORY_MULTILINGUAL_DESTINATION } from '../components/WildcardPageWrapper';

import { TIMEOUTS, withTimeout } from '../helpers/timeout';
import type { LastMinuteDeal } from '../components/flexible-components/components/LastMinuteDeals';
import type { OngoingDealCard } from '../components/flexible-components/components/OngoingDeals';

type Page = {
  optionsPages: TYWOptionPages
  page: WpPageType
  destinations?: DestinationType[]
  festivals?: DestinationType[],
  invisibleDestinations: TYWDestinationType[],
  howItWorksMiniData?: WpPageType
  onshoreEvents?: WpPageType[]
  frontpageExtra?: {}
  includedData?: {}
  destinationBlogs: []
  stats: FeedbackStats,
  intercomArticles: IntercomArticleType[] | null;
  jobs?: WpPageType[];
  years?: WpPageType[];
  artists?: WpPageType[];
  lang?: string | null;
  rawCabins?: Record<string, any>;
  lastMinuteDeals?: LastMinuteDeal[];
  ongoingDeals?: OngoingDealCard[];
  scheduleWeeks?: ScheduleWeek[];
  languageLinks?: LanguageLink[];
}

const Page: NextPage<Page> = (props) => {
  return <WildcardPageWrapper {...props} />;
};

const SITE_HOSTS = ['www.theyachtweek.com', 'theyachtweek.com'];

/**
 * Whether `target` addresses `path` on this site.
 *
 * redirect_to is a free text field: "/mexico", "https://www.theyachtweek.com/mexico"
 * and "https://www.theyachtweek.com/mexico/" all name the same page, and sending
 * a visitor from that page to any of them is a loop.
 */
const samePage = (target: string, path: string): boolean => {
  const strip = (value: string) => (value.replace(/\/+$/, '') || '/');
  if (!/^https?:\/\//i.test(target)) return strip(target) === strip(path);
  try {
    const url = new URL(target);
    const host = url.hostname.toLowerCase();
    const ours = SITE_HOSTS.includes(host) || host === new URL(process.env.DAY8_REACT?.API_URL || 'https://www.theyachtweek.com').hostname;
    return ours && strip(url.pathname) === strip(path);
  } catch {
    return false;
  }
};

export async function getStaticPaths() {
  try {
    // Fetch all pages per category (cached in Redis; same as destinations/festivals)
    const [tofuPages, mofuPages, productPages, destPages, tofuV1Pages, multilingualPages, seoPages] = await Promise.all([
      getWpPagesByCategory(CATEGORY_TOFU_2025, WP_CACHE_TAGS.PAGES_TOFU, { fields: WP_SLIM_PAGE_FIELDS }).catch(() => []),
      getWpPagesByCategory(CATEGORY_MOFU_2025, WP_CACHE_TAGS.PAGES_MOFU, { fields: WP_SLIM_PAGE_FIELDS }).catch(() => []),
      getWpPagesByCategory(CATEGORY_PRODUCT_2025, WP_CACHE_TAGS.PAGES_PRODUCT, { fields: WP_SLIM_PAGE_FIELDS }).catch(() => []),
      getWpPagesByCategory(CATEGORY_DESTINATION_2025, WP_CACHE_TAGS.PAGES_DESTINATION, { fields: WP_SLIM_PAGE_FIELDS }).catch(() => []),
      getWpPagesByCategory(CATEGORY_TOFU_V1_2025, WP_CACHE_TAGS.PAGES_TOFU_V1, { fields: WP_SLIM_PAGE_FIELDS }).catch(() => []),
      getWpPagesByCategory(CATEGORY_MULTILINGUAL_DESTINATION, WP_CACHE_TAGS.PAGES_MULTILINGUAL_DEST, { fields: WP_SLIM_PAGE_FIELDS }).catch(() => []),
      getSeoPages().catch(() => []),
    ]);

    // Combine all pages and deduplicate by ID
    const allPages = [...tofuPages, ...mofuPages, ...productPages, ...destPages, ...tofuV1Pages, ...multilingualPages, ...seoPages];
    const uniquePages = Array.from(
      new Map(allPages.map(page => [page.id, page])).values()
    );

    // seo_page is hierarchical: a child renders at "/parent-slug/child-slug", so
    // its path needs the parent segment in front of it. Everything else is flat.
    const seoById = new Map((seoPages as any[]).map((page: any) => [page.id, page]));
    const pathSegmentsFor = (page: any): string[] => {
      const parent = page?.parent ? seoById.get(page.parent) : null;
      return parent?.slug ? [parent.slug, page.slug] : [page.slug];
    };

    // A seo_page whose only job is to own a URL segment and forward on has a
    // redirect_to. getStaticProps answers those with a `redirect`, which Next
    // refuses to accept while prerendering, so keep them out of the path list
    // and let fallback: 'blocking' serve them on demand.
    const redirectsAway = (page: any): boolean =>
      typeof page?.acf?.seo_page_settings?.redirect_to === 'string'
      && page.acf.seo_page_settings.redirect_to.trim() !== '';

    // The same URL can be owned by two posts: a redirect-only seo_page and an
    // ordinary WP page of the same slug. getStaticProps answers that URL from
    // the seo_page, so the path has to come off the list whichever post put it
    // there — filtering the seo_page alone still leaves the export to fail on
    // the WP page's copy of it.
    const redirectingPaths = new Set(
      (seoPages as any[])
        .filter((page: any) => redirectsAway(page))
        .map((page: any) => pathSegmentsFor(page).join('/'))
    );

    const paths = uniquePages
      .filter((page: any) => !redirectsAway(page))
      .map((page: any) => pathSegmentsFor(page))
      .filter((segments: string[]) => !redirectingPaths.has(segments.join('/')))
      .map((segments: string[]) => ({
        params: {
          slug: segments
        }
      }));

    paths.push({ params: { slug: ['croatia', 'food-packages'] } });
    paths.push({ params: { slug: ['greece', 'food-packages'] } });
    // Add events page explicitly
    paths.push({ params: { slug: ['events'] } });

    // Generate paths for all supported languages (excluding 'en' which uses default route)
    const languages: string[] = await getActiveLanguages().catch(() => []);
    for (const lang of languages) {
      const [langPages, langSeoPages] = await Promise.all([
        wp.pages()
          .param('lang', lang)
          .param('per_page', 100)
          .param('_fields', WP_SLIM_PAGE_FIELDS.join(','))
          .get()
          .catch(() => []),
        getSeoPages(lang).catch(() => []),
      ]);
      // A translated child keeps its parent segment, the same rule the seo_pages
      // below follow: "/es/nuevo-como-funciona/cabanas-nuevas", not a flat
      // "/es/cabanas-nuevas" that the page does not live at. Reading it from the
      // translation groups is also what keeps these paths and the language
      // switcher's links the same URL.
      const langPagePaths = await Promise.all(
        (langPages as WpPageType[]).map(async (page: any) => {
          const segments = await wpPagePathSegments(page, lang).catch(() => []);
          return segments.length ? [lang, ...segments] : null;
        }),
      );
      langPagePaths.forEach((segments) => {
        if (segments) paths.push({ params: { slug: segments as string[] } });
      });
      // seoPagePathSegments keeps the English URL shape for a translation whose
      // parent has not been translated, so the generated paths match what the
      // switcher links to and what getStaticProps resolves.
      const langPaths = await Promise.all(
        (langSeoPages as any[]).map(async (page: any) => {
          if (!page.slug || redirectsAway(page)) return null;
          // A translation usually leaves redirect_to empty and inherits the
          // English one, so it redirects even though its own field is blank.
          // Prerendering it fails the export, same as for the English original.
          if (await seoEnglishRedirectFor(page, lang).catch(() => '')) return null;
          const segments = await seoPagePathSegments(page, lang).catch(() => []);
          return segments.length ? [lang, ...segments] : null;
        }),
      );
      langPaths.forEach((segments) => {
        if (segments) paths.push({ params: { slug: segments as string[] } });
      });
    }

    return {
      paths,
      fallback: 'blocking'
    };
  } catch (error) {
    console.error('Error in getStaticPaths:', error);
    // Return minimal paths on error to prevent build failure
    return {
      paths: [],
      fallback: 'blocking'
    };
  }
}

export const getStaticProps = async ({ params }: { params: { slug: string[] } }) => {
  try {
    const segments = params?.slug;

    // Check if the first segment is a supported language code
    // Which languages exist is read from WordPress, so switching one on in WPML
    // is enough — no env var, no deploy. See getActiveLanguages.
    const activeLanguages: string[] = await getActiveLanguages().catch(() => []);
    const isLangRoute = activeLanguages.includes(segments[0]);
    const lang = isLangRoute ? segments[0] : null;

    // For language routes, the page slug segments come after the lang prefix
    const pageSegments = isLangRoute ? segments.slice(1) : segments;

    // A bare language prefix ("/es", "/nl") has no page of its own; the homepage
    // is not translated, so send it to the English one.
    if (isLangRoute && pageSegments.length === 0) {
      return { redirect: { destination: '/', permanent: false }, revalidate: 60 };
    }

    let howItWorksMiniData = null;
    let frontpageExtra = null;
    let includedData = null;
    let intercomArticles = null;

    // Resolving the page is a chain - the parent has to come back before we can scope the child
    // lookup by it, and ACF only hydrates once we have an id. Everything else on this page is
    // independent of that chain and of the others, so it all runs alongside rather than after.
    // Sequentially this was ~10 upstream round trips; cold, that exceeded the proxy's timeout.
    // Set when a lookup did not answer at all, as opposed to answering that
    // there is no such page. The two have to lead to different places: see the
    // guard below the Promise.all.
    let pageLookupFailed = false;

    const pagePromise = (async () => {
      let parentId;
      let childSlug = pageSegments[0];

      if (pageSegments.length > 1) {
        const parentSlug = pageSegments[0];
        const parentQuery = lang
          ? wp.pages().param('lang', lang).slug(parentSlug).get()
          : wp.pages().slug(parentSlug).get();
        const parentPages = await withTimeout<any[]>(parentQuery, TIMEOUTS.WP_SINGLE, `WP parent "${parentSlug}"`);
        parentId = parentPages.length > 0 ? parentPages[0]?.id : undefined;
        childSlug = pageSegments[1];
      }

      const pageQuery = parentId
        ? (lang
            ? wp.pages().param('lang', lang).slug(childSlug).parent(parentId).get()
            : wp.pages().slug(childSlug).parent(parentId).get())
        : (lang
            ? wp.pages().param('lang', lang).slug(childSlug).get()
            : wp.pages().slug(childSlug).get());

      let found: any[] = [];
      try {
        found = await withTimeout<any[]>(pageQuery, TIMEOUTS.WP_SINGLE, `WP page "${childSlug}"`);
      } catch (e) {
        pageLookupFailed = true;
        console.warn(`[Wildcard] WP pages lookup failed for "${childSlug}", falling back to seo_page`, e);
      }
      let resolved = Array.isArray(found) ? found[0] : undefined;

      // No Page owns this path — seo_page serves from the same root, so try it
      // before giving up. Resolved against the whole path rather than the last
      // segment alone: a child under a parent must only answer on its own URL,
      // or every prefix would serve the same page.
      if (!resolved) {
        resolved = await resolveSeoPageByPath(pageSegments, lang).catch(() => {
          pageLookupFailed = true;
          return undefined;
        });
      }

      return resolved?.id ? await hydrateWpPageAcf(resolved, lang) : resolved;
    })();

    // Mirrors what getFeedbackStats returns on its own error path, so a failed fetch and a
    // failed promise produce the same shape.
    const emptyStats: FeedbackStats = {
      average_nps: 0,
      total_count: 0,
      nps_distribution: {},
      category_ratings: { skipper: 0, hostess: 0, yacht: 0, parties: 0, crew: 0, music: 0 },
    };

    const [
      optionsPages,
      fetchedPage,
      destinations,
      festivals,
      stats,
      invisibleDestinations,
      onshoreEvents,
    ] = await Promise.all([
      // getWpOptionsPages already lays a language's options over the English
      // ones field by field, so an options page nobody has translated yet
      // still arrives with its navbar and footer.
      getWpOptionsPages(lang)
        .catch(() => ({}))
        .then((options: any) => mergeKittyConfigIntoOptions(options as TYWOptionPages, lang)) as Promise<TYWOptionPages>,
      pagePromise,
      getDestinations(false, lang).catch(() => []),
      getFestivals(lang).catch(() => []),
      getFeedbackStats().catch(() => emptyStats) as Promise<FeedbackStats>,
      getInvisibleDestinations().catch(() => []) as Promise<TYWDestinationType[]>,
      isLangRoute
        ? Promise.resolve([] as WpPageType[])
        : getWpPagesByCategory(CATEGORY_ONSHORE_EVENTS, WP_CACHE_TAGS.PAGES_ONSHORE_EVENTS).catch(() => []),
    ]);

    let page = fetchedPage;

    // WordPress not answering is not the same as this path having no page.
    //
    // Every branch below answers a missing page with a `redirect`, and Next
    // refuses a redirect while prerendering - so one unanswered lookup during a
    // deploy fails the whole export, which is what
    // https://nextjs.org/docs/messages/gsp-redirect-during-prerender is about.
    // It is also the likeliest moment for a lookup to fail: a build asks
    // WordPress for everything at once. A 404 that ISR replaces within the
    // minute costs one cold page; a failed export costs the deploy.
    if (!page && pageLookupFailed) {
      console.warn(`[Wildcard] no answer from WordPress for "/${segments.join('/')}" - serving 404 until it answers`);
      return { notFound: true, revalidate: 60 };
    }

    // A language URL built by simply prefixing the English path — "/es/mexico",
    // "/es/mexico/trip-overview" — does not exist, because a translation gets
    // its own slug and need not keep the parent/child shape: the Spanish page
    // here is top-level at "/es/resumen-del-viaje". Those URLs get shared and
    // typed anyway, so rather than 404 them, look the path up in English and
    // forward to wherever that page actually lives in this language.
    //
    // A redirect-only parent has no translation to forward to, so it falls back
    // to its own redirect target and behaves the same in every language. That
    // keeps the rule intact as more pages are added: whatever an English path
    // does, its language-prefixed twin does too, without an editor having to
    // mirror the structure in WPML.
    // The page may simply have moved: giving a page a parent turns "/slug" into
    // "/parent/slug". The bare slug stays linked and shared, so send it on to
    // wherever the page lives now instead of 404ing it. Same language, so this
    // is checked before falling back to English.
    if (!page && pageSegments.length === 1) {
      const canonical = await canonicalSeoPathForSlug(pageSegments[0], lang).catch(() => null);
      if (canonical?.length) {
        const destination = lang ? `/${lang}/${canonical.join('/')}` : `/${canonical.join('/')}`;
        return { redirect: { destination, permanent: false }, revalidate: 60 };
      }
    }

    if (!page && isLangRoute) {
      const englishPage: any = await resolveSeoPageByPath(pageSegments, null).catch(() => undefined);

      if (englishPage?.id) {
        const links = await getSeoPageTranslations(englishPage, null).catch(() => []);
        const translated = links.find((link: { code: string }) => link.code === lang);

        if (translated) {
          return { redirect: { destination: translated.path, permanent: false }, revalidate: 60 };
        }

        const parentRedirect = typeof englishPage.acf?.seo_page_settings?.redirect_to === 'string'
          ? englishPage.acf.seo_page_settings.redirect_to.trim()
          : '';
        if (parentRedirect) {
          return { redirect: { destination: parentRedirect, permanent: false }, revalidate: 60 };
        }

        // Not translated into this language: show the English page rather than 404.
        return { redirect: { destination: `/${pageSegments.join('/')}`, permanent: false }, revalidate: 60 };
      }

      // The same for WP pages: "/es/new-how-it-work" goes to the Spanish page's
      // own slug, or to the English page when there is no Spanish one.
      const wpTarget = await wpPageLanguageRedirect(pageSegments, lang).catch(() => null);
      if (wpTarget) {
        return { redirect: { destination: wpTarget, permanent: false }, revalidate: 60 };
      }

      // Another language's slug under this prefix ("/de/nuevo-como-funciona"):
      // go through the English path, which the lookup above then resolves.
      const englishTarget = await englishPathForTranslatedSlug(pageSegments[pageSegments.length - 1]).catch(() => null);
      if (englishTarget) {
        return { redirect: { destination: `/${lang}${englishTarget}`, permanent: false }, revalidate: 60 };
      }
    }

    // A translated slug without its language prefix ("/nuevo-como-funciona") is
    // not an English page; send it to the English original it translates.
    if (!page && !isLangRoute) {
      const englishTarget = await englishPathForTranslatedSlug(pageSegments[pageSegments.length - 1]).catch(() => null);
      if (englishTarget && !samePage(englishTarget, `/${segments.join('/')}`)) {
        return { redirect: { destination: englishTarget, permanent: false }, revalidate: 60 };
      }
    }

    // Pages give title as { rendered }, the seo_page CPT gives a plain string.
    const pageTitle = typeof page?.title === 'string' ? page.title : page?.title?.rendered;

    if (!page || !pageTitle) {
      return { notFound: true, revalidate: 60 };
    }

    // A parent seo_page exists to own a URL segment, not to be read. When it
    // carries a redirect target, send visitors on rather than rendering an
    // empty shell. Temporary (307) while the target is still being decided —
    // make it permanent once it settles, because a 301 is cached hard by
    // browsers and is painful to take back.
    //
    // A translation may not carry the target at all — whether WPML copies the
    // field is an ACFML setting — so the English original answers for it. The
    // target is a property of the URL segment, not of the language.
    let seoRedirect = typeof page.acf?.seo_page_settings?.redirect_to === 'string'
      ? page.acf.seo_page_settings.redirect_to.trim()
      : '';
    if (!seoRedirect && lang) {
      seoRedirect = await seoEnglishRedirectFor(page, lang).catch(() => '');
    }
    // An editor can type this page's own URL into the field, which browsers
    // follow until they give up. Treat that as no redirect at all and render.
    // An editor may paste the full address rather than a path, so compare what
    // the two actually point at: a target on our own host is reduced to its
    // path first. Anything on another host can never be this page.
    const currentPath = `/${segments.join('/')}`;
    if (seoRedirect && samePage(seoRedirect, currentPath)) {
      console.warn(`[seo_page] redirect_to points at its own URL (${currentPath}), ignoring it`);
      seoRedirect = '';
    }
    if (seoRedirect) {
      return {
        redirect: { destination: seoRedirect, permanent: false },
        revalidate: 60,
      };
    }

    // ACF flexible-content returns `false` (not undefined) when empty, so normalize to an array before using array methods.
    const uiComponents = Array.isArray(page.acf?.day_8_ui_components) ? page.acf.day_8_ui_components : [];
    const intercomArticlesField = uiComponents.find((component: any) => FAQ_LAYOUTS.includes(component.acf_fc_layout))?.intercom_articles || page.acf?.intercom_articles;
    if (intercomArticlesField) {
      intercomArticles = await getFaqs(
        intercomArticlesField.map((article: any) => article.article_id),
        { postId: page.id, url: '/' + segments.join('/') }
      );
    }
    const hasJobPostBlock = uiComponents.some((c: any) => c.acf_fc_layout === 'job_post' || c.acf_fc_layout === 'job-post');
    const hasFlexibleComponents = uiComponents.length > 0;
    const hasYachtLayout = uiComponents.some((c: any) => c.acf_fc_layout === 'yacht_layout');
    const hasMusicResidentsBlock = uiComponents.some((c: any) => c.acf_fc_layout === 'music_residents');
    const needsLastMinuteDeals = uiComponents.some((c: any) => c.acf_fc_layout === 'last_minute_deals');
    // Sailing Schedule rows and the Reviews "weeks" stat both use the funnel weeks.
    // SEO pages use them too, wherever a field carries a {{dates}} / {{price}}
    // token — those resolve against the same weeks, so the fetch has to happen
    // for them as well or the token renders empty.
    const seoBlocks = Array.isArray(page.acf?.seo_flexible_components) ? page.acf.seo_flexible_components : [];
    const seoUsesLiveWeeks = seoBlocksUseLiveWeeks(seoBlocks);
    const needsScheduleWeeks = seoUsesLiveWeeks
      || uiComponents.some((c: any) => ['sailing_schedule', 'reviews'].includes(c.acf_fc_layout));
    const ongoingDealsBlock = uiComponents.find((c: any) => c.acf_fc_layout === 'ongoing_deals');
    const needsOngoingDeals = Boolean(ongoingDealsBlock);
    const isMultilingualDestinationPage = page.categories?.includes(CATEGORY_MULTILINGUAL_DESTINATION);
    const needsRawCabins = hasYachtLayout || isMultilingualDestinationPage || needsLastMinuteDeals;
    const [jobsRes, yearsRes, artistsRes, rawCabins, ylDestinations, slimData, dealsEndAt] = await Promise.all([
      hasJobPostBlock ? getJobs().catch(() => []) : Promise.resolve([]),
      hasFlexibleComponents ? getYears().catch(() => []) : Promise.resolve([]),
      hasMusicResidentsBlock ? getArtists().catch(() => []) : Promise.resolve([]),
      needsRawCabins ? getYlCabins().catch(() => ({})) : Promise.resolve({}),
      (needsLastMinuteDeals || needsScheduleWeeks || hasYachtLayout) ? getYlDestinations().catch(() => []) : Promise.resolve([]),
      needsOngoingDeals
        ? getYachtListSlim({ company: DEFAULT_COMPANY_ID }).catch(() => null)
        : Promise.resolve(null),
      needsOngoingDeals ? getDealsEndAt().catch(() => null) : Promise.resolve(null),
    ]);
    const jobs = (jobsRes || []) as WpPageType[];
    const years = (yearsRes || []) as WpPageType[];
    const artists = (Array.isArray(artistsRes) ? artistsRes : []) as WpPageType[];
    const lastMinuteDeals = needsLastMinuteDeals
      ? buildLastMinuteDeals(ylDestinations as any[], {
          cabins: rawCabins,
        })
      : [];
    const ongoingDeals = needsOngoingDeals
      ? (buildOngoingDealsFromSlim(slimData, { dealsEndAt }) as OngoingDealCard[])
      : [];
    if (hasYachtLayout) {
      page = { ...page, acf: { ...page.acf, day_8_ui_components: attachYachtLayoutPrices(uiComponents, ylDestinations as any[], rawCabins as Record<string, any>, publicDestinationIds(festivals as any[])) } };
    }
    const scheduleWeeks = needsScheduleWeeks
      ? buildScheduleWeeks(ylDestinations as any[], festivals as any[])
      : [];

    // Tokens are resolved here rather than in each section, so no field can
    // ship a raw {{dates}} just because its component forgot to ask.
    page = withResolvedSeoTokens(page, scheduleWeeks);

    // The switcher cannot work this out on the client: a translation has its own
    // slug (and may not even share the parent/child shape), so the paths are
    // resolved here from WPML.
    const languageLinks = seoBlocks.length
      ? await getSeoPageTranslations(page, lang).catch(() => [])
      : page.type === 'page'
        ? await getWpPageTranslations(page, lang).catch(() => [])
        : [];

    return {
      props: {
        optionsPages,
        jobs,
        years,
        artists,
        stats,
        destinations: (destinations as any[]).map((d) => sanitizeDestinationResponse(d)) as unknown as DestinationType[],
        festivals: (festivals as any[]).map((d) => sanitizeDestinationResponse(d)) as unknown as DestinationType[],
        invisibleDestinations,
        onshoreEvents: onshoreEvents.map((d: WpPageType) => sanitizeWpResponse(d)),
        intercomArticles,
        rawCabins: rawCabins as Record<string, any>,
        lastMinuteDeals,
        ongoingDeals,
        scheduleWeeks,
        languageLinks,
        page: {
          ...sanitizeWpResponse(page)
        },
        howItWorksMiniData,
        frontpageExtra,
        includedData,
        lang: lang ?? null,
      },
      // Pages revalidate every 60 seconds
      revalidate: 60
    };
  } catch (e) {
    return { notFound: true, revalidate: 60 };
  }
};

export default Page;
